// @ts-strict-ignore
import { isArray } from 'lodash-es'
import { Approval } from 'constants/General'
import { AmountSummary, ApprovalPreferences } from 'types/approvals'
import { useAccountsContext } from './context/useAccountsContext'
import { useProfileContext } from './context/useProfileContext'
import { useAccountPreferencesQuery, useExchangeRateQuery } from './queries'
import { useApprovalsPreference } from './useApprovalsPreference'

const useApprovalConfigUtils = (amountSummary: AmountSummary) => {
  const { profile: { currencyCode } = {} } = useProfileContext()
  const { data: accountPreferences, isFetching: isFetchingAccountPreferences } =
    useAccountPreferencesQuery()
  const baseCurrency =
    (!isFetchingAccountPreferences && (accountPreferences?.currency || currencyCode)) || ''
  const { currentAccount } = useAccountsContext()
  const currentAccountReference = currentAccount?.reference
  const isApprovalQueryEnabled = !!currentAccountReference
  const { approvalFlowPreferences, isFetchingApprovalPreference } = useApprovalsPreference(
    currentAccountReference,
    isApprovalQueryEnabled
  )
  const isExchangeRateQueryEnabled = !!(
    baseCurrency !== amountSummary?.currency &&
    amountSummary?.amount &&
    amountSummary?.currency &&
    baseCurrency
  )
  const { data: exchangeRate } = useExchangeRateQuery({
    fromCurrency: amountSummary?.currency,
    toCurrency: baseCurrency,
    amount: amountSummary?.amount,
    enabled: isExchangeRateQueryEnabled,
  })

  return {
    approvalAmount: exchangeRate?.value || amountSummary?.amount,
    approvalFlowPreferences,
    isFetchingApprovalPreference,
  }
}

/**
 * Checks if Approvals is required for a payment flow or not
 * if no paymentFlow is passed, it will return all the flows with their corresponding boolean values
 *
 *
 * @param {string} paymentFlow - any of the supported flow types
 * @param {AmountSummary} amountSummary - option to check if approval is required for amount
 */
export const useApprovalsConfigForFlow = (
  paymentFlow?: string,
  amountSummary?: AmountSummary
): {
  approvalPreferences?: ApprovalPreferences[]
  approvalsIsRequiredForFlow?: boolean
  isFetchingApprovalPreference?: boolean
  mayRequireApprovalForAmount?: boolean
} => {
  const { approvalAmount, approvalFlowPreferences, isFetchingApprovalPreference } =
    useApprovalConfigUtils(amountSummary)

  const approvalPreferences = approvalFlowPreferences?.preferences || approvalFlowPreferences
  const isValidApprovalPreferenceFormat = isArray(approvalPreferences)
  if (!paymentFlow && isValidApprovalPreferenceFormat) {
    return { approvalPreferences }
  }

  const indexOfPaymentFlow =
    isValidApprovalPreferenceFormat &&
    approvalPreferences?.findIndex(
      approvalPreference =>
        approvalPreference?.flow === paymentFlow && approvalPreference?.approvalsRequired
    )
  let approvalsIsRequiredForFlow = isValidApprovalPreferenceFormat && indexOfPaymentFlow !== -1
  const approvalLimits = approvalFlowPreferences?.limits
  if (
    amountSummary?.amount &&
    amountSummary?.currency &&
    approvalsIsRequiredForFlow &&
    approvalLimits
  ) {
    const amountValue = Number(approvalAmount)
    const approvalLimitsCheck = approvalLimits?.find(limit =>
      limit?.maxAmount === Approval.RULES.INFINITY
        ? amountValue >= Number(limit?.minAmount)
        : amountValue >= Number(limit?.minAmount) && amountValue <= Number(limit?.maxAmount)
    )
    approvalsIsRequiredForFlow = !!Number(approvalLimitsCheck?.approvalsCount)
  }

  return { approvalsIsRequiredForFlow, isFetchingApprovalPreference }
}
